/**
 * Shared PDF chrome for the Retail & Tea Room Sales box reports — the same
 * NYMR-green header the page's own PDF export uses (SalesDashboard::exportPdf),
 * a direct download (never a popup), and one place for the money format.
 */
import { jsPDF } from "jspdf";
import autoTable, { type UserOptions } from "jspdf-autotable";

export const GREEN: [number, number, number] = [31, 77, 63];
export const GREEN_DEEP: [number, number, number] = [11, 61, 46];
export const CREAM: [number, number, number] = [250, 248, 242];

export function printedOn(): string {
  return new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

const gbp = new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const gbp0 = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
export function pounds(n: number, whole = false): string {
  const v = whole ? gbp0.format(Math.round(Math.abs(n))) : gbp.format(Math.abs(n));
  return `${n < 0 ? "−" : ""}£${v}`.replace("−", "-");
}
export function signedPounds(n: number): string {
  if (Math.abs(n) < 0.005) return "£0.00";
  return `${n > 0 ? "+" : "-"}£${gbp.format(Math.abs(n))}`;
}
export function prettyIso(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${m[3]} ${months[Number(m[2]) - 1]} ${m[1]}`;
}

/** A4 doc with the green title + grey subtitle; returns the doc and the y to start content at. */
export function startPdf(title: string, subtitle: string, orientation: "landscape" | "portrait" = "landscape"): { doc: jsPDF; y: number } {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...GREEN_DEEP);
  doc.text(title, 10, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90);
  const lines = doc.splitTextToSize(`${subtitle}  ·  printed ${printedOn()}`, doc.internal.pageSize.getWidth() - 20) as string[];
  lines.forEach((ln, i) => doc.text(ln, 10, 17 + i * 4));
  return { doc, y: 17 + lines.length * 4 + 3 };
}

/** A section heading in the body; returns the next y. */
export function sectionTitle(doc: jsPDF, text: string, y: number): number {
  if (y > doc.internal.pageSize.getHeight() - 30) {
    doc.addPage();
    y = 14;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...GREEN_DEEP);
  doc.text(text, 10, y);
  return y + 2;
}

/** The page's standard table look; returns the y after the table. */
export function table(doc: jsPDF, opts: UserOptions & { startY: number }): number {
  autoTable(doc, {
    margin: { left: 8, right: 8 },
    styles: { fontSize: 8, cellPadding: 1.4, overflow: "linebreak", halign: "right" },
    headStyles: { fillColor: GREEN, textColor: 255, fontStyle: "bold", halign: "right" },
    alternateRowStyles: { fillColor: CREAM },
    footStyles: { fillColor: [230, 236, 232], textColor: 20, fontStyle: "bold", halign: "right" },
    ...opts,
  });
  const finalY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY;
  return (finalY ?? opts.startY) + 8;
}

/** Direct download via doc.save() — never window.open. */
export function savePdf(doc: jsPDF, name: string): void {
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`Page ${p} of ${total}`, doc.internal.pageSize.getWidth() - 10, doc.internal.pageSize.getHeight() - 6, { align: "right" });
  }
  doc.save(name);
}
